import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Alert,
  Button,
  TextField,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import {
  DataGrid,
  GridColDef,
} from '@mui/x-data-grid';
import useReportStore from '../stores/useReportStore';
import useAuthStore from '../stores/useAuthStore';
import ExportExcelButton from '../components/ExportExcelButton';
import ReceiptPDF from '../components/ReceiptPdf';
import 'dayjs/locale/tr';
dayjs.locale('tr');

function ReportPage() {
  const { reportData, isLoading, error, fetchPaymentReport } = useReportStore();
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs());
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());

  const [filters, setFilters] = useState({
    CollectionDate: '',
    CurrentCode: '',
    CurrentAccountName: '',
    Amount: '',
    Installment: '',
  });

  const handleGetReport = () => {
    if (!startDate || !endDate) return;

    const startStr = startDate.format('DD.MM.YYYY');
    const endStr = endDate.format('DD.MM.YYYY');

    const { user, adminInfo } = useAuthStore.getState();
    const currentCode = adminInfo?.isAdmin ? null : user?.CariKod;

    fetchPaymentReport(startStr, endStr, currentCode ?? '');
  };

  const filteredRows = useMemo(() => {
    return reportData.filter((item) => {
      return Object.entries(filters).every(([key, value]) => {
        if (!value.trim()) return true;
        const fieldValue = item[key as keyof typeof item];
        return fieldValue?.toString().toLowerCase().includes(value.toLowerCase());
      });
    });
  }, [filters, reportData]);

  const columns: GridColDef[] = [
    {
      field: 'CollectionDate',
      headerName: 'Tahsilat Tarihi',
      flex: 1,
      headerClassName: 'header-bold',
      align: 'left',
      valueFormatter: (params) =>
        dayjs(params).format('DD.MM.YYYY HH:mm'),
      renderHeader: () => (
        <Box>
          <Typography fontWeight={400} fontSize="1.1rem" mt={1.5}>Tahsilat Tarihi</Typography>
          <TextField
            variant="standard"
            placeholder="Ara..."
            value={filters.CollectionDate}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, CollectionDate: e.target.value }))
            }
            onClick={(e) => e.stopPropagation()}
            fullWidth
          />
        </Box>
      ),
    },
    {
      field: 'CurrentCode',
      headerName: 'Cari Kodu',
      flex: 1,
      headerClassName: 'header-bold',
      align: 'left',
      renderHeader: () => (
        <Box>
          <Typography fontWeight={400} fontSize="1.1rem" mt={1.5}>Cari Kodu</Typography>
          <TextField
            variant="standard"
            placeholder="Ara..."
            value={filters.CurrentCode}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, CurrentCode: e.target.value }))
            }
            onClick={(e) => e.stopPropagation()}
            fullWidth
          />
        </Box>
      ),
    },
    {
      field: 'CurrentAccountName',
      headerName: 'Cari Adı',
      flex: 2,
      headerClassName: 'header-bold',
      align: 'left',
      renderHeader: () => (
        <Box>
          <Typography fontWeight={400} fontSize="1.1rem" mt={1.5}>Cari Adı</Typography>
          <TextField
            variant="standard"
            placeholder="Ara..."
            value={filters.CurrentAccountName}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, CurrentAccountName: e.target.value }))
            }
            onClick={(e) => e.stopPropagation()}
            fullWidth
          />
        </Box>
      ),
    },
    {
      field: 'Amount',
      headerName: 'Tutar',
      flex: 1,
      headerClassName: 'header-bold',
      align: 'right',
      valueFormatter: (params) => {
        const raw = Number(params);
        if (isNaN(raw)) return '';
        return raw.toLocaleString('tr-TR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      },
      renderHeader: () => (
        <Box>
          <Typography fontWeight={400} fontSize="1.1rem" mt={1.5}>Tutar</Typography>
          <TextField
            variant="standard"
            placeholder="Ara..."
            value={filters.Amount}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, Amount: e.target.value }))
            }
            onClick={(e) => e.stopPropagation()}
            fullWidth
          />
        </Box>
      ),
    },
    {
      field: 'Installment',
      headerName: 'Taksit Sayısı',
      flex: 1,
      headerClassName: 'header-bold',
      align: 'right',
      renderHeader: () => (
        <Box>
          <Typography fontWeight={400} fontSize="1.1rem" mt={1.5}>Taksit</Typography>
          <TextField
            variant="standard"
            placeholder="Ara..."
            value={filters.Installment}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, Installment: e.target.value }))
            }
            onClick={(e) => e.stopPropagation()}
            fullWidth
          />
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: 'Makbuz',
      flex: 1,
      headerClassName: 'header-bold',
      align: 'center',
      renderCell: (params) => (
        <ReceiptPDF id={params.row.Id?.toString() || ''} />
      ),
    },
  ];

  return (
    <Box sx={{ height: '80vh', display: 'flex', flexDirection: 'column', p: 2 }}>
      {/* Tarih seçim ve butonlar */}
      <Box sx={{ mb: 2 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='tr'>
          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <DatePicker
              label="Başlangıç Tarihi"
              value={startDate}
              onChange={(newVal) => setStartDate(newVal)}
              format="DD.MM.YYYY"
            />
            <DatePicker
              label="Bitiş Tarihi"
              value={endDate}
              onChange={(newVal) => setEndDate(newVal)}
              format="DD.MM.YYYY"
            />
            <Button variant="contained" onClick={handleGetReport}>
              Rapor Al
            </Button>
            <ExportExcelButton
              rows={filteredRows}
              columns={columns.map((col) => ({
                field: col.field,
                headerName: col.headerName ?? col.field,
              }))}
              fileName="Tahsilat_Raporu"
              formatters={{
                Amount: (val: any) =>
                  Number(val).toLocaleString('tr-TR', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }),
                CollectionDate: (val: string | number | dayjs.Dayjs | Date | null | undefined) => dayjs(val).format('DD.MM.YYYY HH:mm'),
              }}
            />
          </Box>
        </LocalizationProvider>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        {isLoading && <Alert severity="info" sx={{ mt: 2 }}>Yükleniyor...</Alert>}
      </Box>

      {/* Tablo */}
      <Box sx={{ flexGrow: 1 }}>
        <DataGrid
          rows={filteredRows.map((item, index) => ({ id: index, ...item }))}
          columns={columns}
          loading={isLoading}
          sx={{
            height: '100%',
            width: '100%',
            '& .header-bold': {
              fontWeight: 700,
              fontSize: '1.1rem',
            },
          }}
          slots={{
            noRowsOverlay: () => (
              <Typography p={2} color="text.secondary" textAlign="center">
                Görüntülenecek rapor verisi bulunamadı.
              </Typography>
            ),
          }}
        />
      </Box>
    </Box>
  );
}

export default ReportPage;
